import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, getDocs, updateDoc, doc, deleteDoc, orderBy, serverTimestamp, getDoc, setDoc } from 'firebase/firestore';

import { db, auth } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import RevenueChart from '../components/ui/RevenueChart';

export default function AdminDashboard({ db, auth, onExit, navigateTo }) {
    const [activeTab, setActiveTab] = useState('overview');
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // Data Stores
    const [stats, setStats] = useState({ revenue: 0, activeUsers: 0, pendingBookings: 0, openTickets: 0, openClaims: 0 });
    const [tickets, setTickets] = useState([]);
    const [users, setUsers] = useState([]);
    const [listings, setListings] = useState([]);
    const [bookings, setBookings] = useState([]);

    const userMap = useMemo(() => {
        return users.reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
        }, {});
    }, [users]);

    useEffect(() => {
        const fetchAdminData = async () => {
            setLoading(true);
            
            const fetchCollection = async (queryRef) => {
                try {
                    const snap = await getDocs(queryRef);
                    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
                } catch (err) {
                    console.warn("Admin fetch failed for a collection (check permissions):", err.code);
                    return []; 
                }
            };

            const [usersData, listingsData, bookingsData, ticketsData] = await Promise.all([
                fetchCollection(collection(db, "users")),
                fetchCollection(collection(db, "listings")),
                fetchCollection(collection(db, "bookings")),
                fetchCollection(query(collection(db, "support_tickets"), orderBy("createdAt", "desc")))
            ]);

            setUsers(usersData);
            setListings(listingsData);
            setBookings(bookingsData);
            setTickets(ticketsData);

            const totalRevenue = bookingsData
                .filter(b => b.status === 'paid' || b.status === 'completed')
                .reduce((acc, b) => acc + (b.totalCost || 0), 0);

            setStats({
                revenue: totalRevenue,
                activeUsers: usersData.length,
                pendingBookings: bookingsData.filter(b => b.status === 'pending').length,
                openTickets: ticketsData.filter(t => t.status === 'open').length,
                openClaims: bookingsData.filter(b => b.claimStatus === 'submitted').length
            });

            setLoading(false);
        };

        if (db) fetchAdminData();
    }, [db]);

    const handleResolveTicket = async (ticketId) => {
        try {
            await updateDoc(doc(db, "support_tickets", ticketId), { status: 'resolved', resolvedAt: serverTimestamp() });
            setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: 'resolved' } : t));
        } catch (err) { alert("Failed to update ticket."); }
    };

    const handleDeleteListing = async (listingId) => {
        if(!window.confirm("Delete this listing permanently?")) return;
        await deleteDoc(doc(db, "listings", listingId));
        setListings(prev => prev.filter(l => l.id !== listingId));
    };

    const handleToggleFeatured = async (listingId, currentStatus) => {
        try {
            await updateDoc(doc(db, "listings", listingId), { isFeatured: !currentStatus });
            setListings(prev => prev.map(l => l.id === listingId ? { ...l, isFeatured: !currentStatus } : l));
        } catch (err) { alert("Failed to update featured status."); }
    };

    const handleBanUser = async (userId, currentStatus) => {
        if(!window.confirm(`Confirm ${currentStatus ? 'unban' : 'ban'} user?`)) return;
        await updateDoc(doc(db, "users", userId), { isBanned: !currentStatus });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, isBanned: !currentStatus } : u));
    };

    const handleUpdateClaim = async (bookingId, newClaimStatus) => {
        try {
            await updateDoc(doc(db, "bookings", bookingId), { claimStatus: newClaimStatus, claimResolvedAt: serverTimestamp() });
            setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, claimStatus: newClaimStatus } : b));
        } catch (err) { alert("Failed to update claim."); }
    };

    const renderContent = () => {
        if (loading) return <div className="flex h-full items-center justify-center"><div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div></div>;
        
        switch (activeTab) {
            case 'overview': return <OverviewTab stats={stats} recentBookings={bookings.slice(0, 8)} allBookings={bookings} userMap={userMap} />;
            case 'support': return <SupportInbox tickets={tickets} onResolve={handleResolveTicket} />;
            case 'claims': return <AdminClaimsTab bookings={bookings} onUpdateClaim={handleUpdateClaim} />;
            case 'bookings': return <BookingsTable bookings={bookings} navigateTo={navigateTo} userMap={userMap} />;
            case 'users': return <UserTable users={users} onBan={handleBanUser} />;
            case 'listings': return <ListingTable listings={listings} onDelete={handleDeleteListing} onToggleFeatured={handleToggleFeatured} userMap={userMap} />;
            case 'settings': return <AdminSettings db={db} />;
            default: return <OverviewTab stats={stats} recentBookings={bookings} allBookings={bookings} userMap={userMap} />;
        }
    };

    return (
        <div className="flex h-screen bg-gray-50 text-gray-800 font-sans overflow-hidden">
            {isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-gray-900/60 z-40 md:hidden backdrop-blur-sm" 
                    onClick={() => setIsSidebarOpen(false)} 
                />
            )}

            <aside className={`fixed inset-y-0 left-0 z-50 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 bg-gray-900 text-gray-300 transition-all duration-300 flex flex-col ${isSidebarCollapsed ? 'md:w-20' : 'md:w-64'} w-64 md:w-auto shadow-2xl md:shadow-none`}>
                <div className="h-16 flex items-center justify-between px-4 bg-gray-950 flex-shrink-0">
                    <div className={`flex items-center ${isSidebarCollapsed ? 'md:hidden' : ''}`}>
                        <span className="font-bold text-lg text-white tracking-wider">HOST-IT <span className="text-pink-500">ADMIN</span></span>
                    </div>
                    <button onClick={() => setSidebarCollapsed(!isSidebarCollapsed)} className="hidden md:block p-2 hover:bg-gray-800 rounded text-gray-400">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
                    </button>
                    <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 hover:bg-gray-800 rounded text-gray-400">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                
                <nav className="flex-1 py-6 space-y-1 px-2 overflow-y-auto custom-scrollbar">
                    <NavItem icon="📊" label="Overview" id="overview" active={activeTab} setTab={(id) => { setActiveTab(id); setIsSidebarOpen(false); }} collapsed={isSidebarCollapsed} />
                    <NavItem icon="📅" label="Bookings" id="bookings" active={activeTab} setTab={(id) => { setActiveTab(id); setIsSidebarOpen(false); }} collapsed={isSidebarCollapsed} badge={stats.pendingBookings} />
                    <NavItem icon="🛡️" label="Claims" id="claims" active={activeTab} setTab={(id) => { setActiveTab(id); setIsSidebarOpen(false); }} collapsed={isSidebarCollapsed} badge={stats.openClaims} />
                    <NavItem icon="🎫" label="Support" id="support" active={activeTab} setTab={(id) => { setActiveTab(id); setIsSidebarOpen(false); }} collapsed={isSidebarCollapsed} badge={stats.openTickets} />
                    <NavItem icon="👥" label="Users" id="users" active={activeTab} setTab={(id) => { setActiveTab(id); setIsSidebarOpen(false); }} collapsed={isSidebarCollapsed} />
                    <NavItem icon="📦" label="Listings" id="listings" active={activeTab} setTab={(id) => { setActiveTab(id); setIsSidebarOpen(false); }} collapsed={isSidebarCollapsed} />
                    <NavItem icon="⚙️" label="Settings" id="settings" active={activeTab} setTab={(id) => { setActiveTab(id); setIsSidebarOpen(false); }} collapsed={isSidebarCollapsed} />
                </nav>

                <div className="p-4 border-t border-gray-800 flex-shrink-0">
                    <button onClick={onExit} className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg hover:bg-red-500/10 text-red-400 transition-colors ${isSidebarCollapsed ? 'md:justify-center' : ''}`}>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        <span className={`font-medium ${isSidebarCollapsed ? 'md:hidden' : ''}`}>Exit Panel</span>
                    </button>
                </div>
            </aside>

            <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
                <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-8 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setIsSidebarOpen(true)} className="md:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-100">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
                        </button>
                        <h1 className="text-xl md:text-2xl font-bold text-gray-900 capitalize">{activeTab}</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500 hidden sm:block">Administrator</span>
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
                    {renderContent()}
                </div>
            </main>
        </div>
    );
}

function NavItem({ icon, label, id, active, setTab, collapsed, badge = 0 }) {
    return (
        <button
            onClick={() => setTab(id)}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all group ${
                active === id 
                    ? 'bg-pink-600 text-white shadow-md' 
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            } ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? label : ''}
        >
            <span className="text-xl group-hover:scale-110 transition-transform">{icon}</span>
            {!collapsed && (
                <div className="flex-1 flex justify-between items-center">
                    <span className="font-medium">{label}</span>
                    {badge > 0 && <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{badge}</span>}
                </div>
            )}
        </button>
    );
}

function OverviewTab({ stats, recentBookings, allBookings, userMap }) {
    return (
        <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard title="Total Revenue" value={`$${stats.revenue.toLocaleString()}`} trend="+12%" icon="💰" color="emerald" />
                <KPICard title="Active Users" value={stats.activeUsers} trend="+5%" icon="👥" color="blue" />
                <KPICard title="Pending Bookings" value={stats.pendingBookings} trend="Needs Action" icon="📅" color="amber" />
                <KPICard title="Open Claims" value={stats.openClaims} trend={stats.openClaims > 0 ? "Action Req" : "All Good"} icon="🛡️" color="purple" />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-gray-800 text-lg">Platform Performance</h3>
                    <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">Last 6 Months</div>
                </div>
                <RevenueChart bookings={allBookings} />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="font-bold text-gray-800">Recent Transactions</h3>
                    <span className="text-xs text-gray-500">Latest 8 orders</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-6 py-3">Order ID</th>
                                <th className="px-6 py-3">Customer</th>
                                <th className="px-6 py-3">Vendor</th>
                                <th className="px-6 py-3">Amount</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {recentBookings.map(b => (
                                <tr key={b.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-3 font-mono text-xs text-gray-500">#{b.bookingNumber || b.id.substring(0,6)}</td>
                                    <td className="px-6 py-3 font-medium text-gray-900">{b.renterName || 'Guest'}</td>
                                    <td className="px-6 py-3 text-gray-600">{userMap[b.vendorId]?.vendorName || 'Unknown Vendor'}</td>
                                    <td className="px-6 py-3 font-bold">${b.totalCost}</td>
                                    <td className="px-6 py-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                            b.status === 'paid' ? 'bg-green-100 text-green-700' : 
                                            b.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                                            ['cancelled', 'declined'].includes(b.status) ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                                        }`}>{b.status}</span>
                                    </td>
                                    <td className="px-6 py-3 text-gray-500">{new Date(b.createdAt?.seconds * 1000 || Date.now()).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function AdminClaimsTab({ bookings, onUpdateClaim }) {
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [filter, setFilter] = useState('all');

    const claims = bookings.filter(b => b.claimStatus).filter(b => {
        if (filter === 'submitted') return b.claimStatus === 'submitted';
        if (filter === 'resolved') return ['approved', 'denied'].includes(b.claimStatus);
        return true;
    }).sort((a, b) => {
        const timeA = a.claimDate?.seconds || 0;
        const timeB = b.claimDate?.seconds || 0;
        return timeB - timeA;
    });

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex gap-2 bg-white p-1 rounded-lg w-fit shadow-sm border border-gray-200">
                {['all', 'submitted', 'resolved'].map(f => (
                    <button 
                        key={f} 
                        onClick={() => setFilter(f)} 
                        className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition ${filter === f ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-6 py-3">Order ID</th>
                                <th className="px-6 py-3">Renter</th>
                                <th className="px-6 py-3">Item</th>
                                <th className="px-6 py-3">Date Submitted</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {claims.map(c => (
                                <tr key={c.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 font-mono text-xs text-gray-500">#{c.bookingNumber || c.id.slice(0,6)}</td>
                                    <td className="px-6 py-4 font-medium text-gray-900">{c.renterName}</td>
                                    <td className="px-6 py-4 text-gray-600 truncate max-w-[200px]">{c.listingTitle}</td>
                                    <td className="px-6 py-4 text-gray-500">{new Date(c.claimDate?.seconds * 1000 || Date.now()).toLocaleDateString()}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${c.claimStatus === 'submitted' ? 'bg-yellow-100 text-yellow-700' : c.claimStatus === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{c.claimStatus}</span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button onClick={() => setSelectedClaim(c)} className="bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition">View Details</button>
                                    </td>
                                </tr>
                            ))}
                            {claims.length === 0 && <tr><td colSpan="6" className="text-center py-12 text-gray-400 italic">No claims found.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
            {selectedClaim && <AdminClaimModal claim={selectedClaim} onClose={() => setSelectedClaim(null)} onUpdate={onUpdateClaim} />}
        </div>
    );
}

function AdminClaimModal({ claim, onClose, onUpdate }) {
    const [isProcessing, setIsProcessing] = useState(false);

    const handleAction = async (status) => {
        setIsProcessing(true);
        await onUpdate(claim.id, status);
        setIsProcessing(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 flex-shrink-0">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2"><span>🛡️</span> Claim Details</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-800 text-2xl leading-none">&times;</button>
                </div>
                <div className="p-6 overflow-y-auto flex-grow custom-scrollbar space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <div><span className="text-gray-500 font-bold uppercase text-[10px] tracking-wider block mb-1">Order Number</span><span className="font-mono font-bold text-gray-900">#{claim.bookingNumber || claim.id.slice(0,6)}</span></div>
                        <div><span className="text-gray-500 font-bold uppercase text-[10px] tracking-wider block mb-1">Renter</span><span className="font-bold text-gray-900">{claim.renterName}</span></div>
                        <div><span className="text-gray-500 font-bold uppercase text-[10px] tracking-wider block mb-1">Item</span><span className="font-bold text-gray-900 truncate block">{claim.listingTitle}</span></div>
                        <div><span className="text-gray-500 font-bold uppercase text-[10px] tracking-wider block mb-1">Current Status</span><span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${claim.claimStatus === 'submitted' ? 'bg-yellow-100 text-yellow-700' : claim.claimStatus === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{claim.claimStatus}</span></div>
                    </div>
                    
                    <div>
                        <h4 className="font-bold text-gray-800 mb-3 text-lg">Incident Description</h4>
                        <div className="bg-gray-50 p-5 rounded-xl border border-gray-100 text-gray-700 text-sm whitespace-pre-wrap leading-relaxed shadow-inner">
                            {claim.claimDetails?.description || 'No description provided.'}
                        </div>
                    </div>
                    
                    {claim.claimDetails?.photoUrls?.length > 0 && (
                        <div>
                            <h4 className="font-bold text-gray-800 mb-3 text-lg">Evidence Photos</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {claim.claimDetails.photoUrls.map((url, i) => (
                                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block relative aspect-square rounded-xl overflow-hidden border border-gray-200 shadow-sm group">
                                        <img src={url} alt={`Evidence ${i+1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity backdrop-blur-sm"><span className="text-white text-sm font-bold shadow-md">View Full</span></div>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {claim.claimStatus === 'submitted' && (
                    <div className="p-5 border-t border-gray-100 bg-gray-50 flex gap-3 justify-end flex-shrink-0">
                        <button onClick={() => handleAction('denied')} disabled={isProcessing} className="px-6 py-2.5 bg-red-50 text-red-600 font-bold rounded-lg hover:bg-red-100 transition disabled:opacity-50">Deny Claim</button>
                        <button onClick={() => handleAction('approved')} disabled={isProcessing} className="px-6 py-2.5 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 shadow-md transition disabled:opacity-50 flex items-center gap-2">
                            {isProcessing ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : '✓'} Approve Claim
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function BookingsTable({ bookings, navigateTo, userMap }) {
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const filtered = bookings.filter(b => {
        const matchesSearch = 
            (b.renterName || '').toLowerCase().includes(search.toLowerCase()) ||
            (b.bookingNumber || '').toLowerCase().includes(search.toLowerCase()) ||
            (b.listingTitle || '').toLowerCase().includes(search.toLowerCase()) ||
            (userMap[b.vendorId]?.vendorName || '').toLowerCase().includes(search.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-fade-in">
            <div className="p-4 border-b border-gray-200 flex gap-4 flex-wrap justify-between">
                <input 
                    type="text" 
                    placeholder="Search by ID, Customer, Vendor, or Item..." 
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="flex-grow max-w-md px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                />
                <select 
                    value={statusFilter} 
                    onChange={e => setStatusFilter(e.target.value)} 
                    className="px-4 py-2 border rounded-lg text-sm bg-white cursor-pointer hover:bg-gray-50"
                >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="awaitingPayment">Awaiting Payment</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="completed">Completed</option>
                </select>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-xs">
                        <tr>
                            <th className="px-6 py-4">Order ID</th>
                            <th className="px-6 py-4">Details</th>
                            <th className="px-6 py-4">Parties</th>
                            <th className="px-6 py-4">Total</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map(b => (
                            <tr key={b.id} className="hover:bg-gray-50 transition">
                                <td className="px-6 py-4 font-mono text-xs text-gray-500">
                                    {b.bookingNumber || b.id.slice(0,6)}
                                    <div className="text-[10px] text-gray-400 mt-1">{new Date(b.date).toLocaleDateString()}</div>
                                </td>
                                <td className="px-6 py-4">
                                    <p className="font-semibold text-gray-900 truncate max-w-[200px]">{b.listingTitle}</p>
                                    <p className="text-xs text-gray-500">Qty: {b.quantity}</p>
                                </td>
                                <td className="px-6 py-4">
                                    <p className="text-xs text-gray-500"><strong className="text-gray-700">Renter:</strong> {b.renterName || 'Guest'}</p>
                                    <p className="text-xs text-gray-500"><strong className="text-gray-700">Vendor:</strong> {userMap[b.vendorId]?.vendorName || 'Unknown'}</p>
                                </td>
                                <td className="px-6 py-4 font-bold text-gray-900">${(b.totalCost || 0).toFixed(2)}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                        ['paid', 'completed', 'delivered', 'returned'].includes(b.status) ? 'bg-green-100 text-green-700' :
                                        ['cancelled', 'declined'].includes(b.status) ? 'bg-red-100 text-red-700' :
                                        'bg-yellow-100 text-yellow-700'
                                    }`}>
                                        {b.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <button 
                                        onClick={() => navigateTo('bookingDetail', { booking: b })}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs font-bold transition"
                                    >
                                        View
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function KPICard({ title, value, trend, icon, color }) {
    const colors = {
        emerald: 'bg-emerald-50 text-emerald-600',
        blue: 'bg-blue-50 text-blue-600',
        amber: 'bg-amber-50 text-amber-600',
        purple: 'bg-purple-50 text-purple-600'
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition min-w-0">
            <div className="flex justify-between items-start">
                <div className="min-w-0 pr-2">
                    <p className="text-sm font-medium text-gray-500 truncate" title={title}>{title}</p>
                    <h3 className="text-3xl font-bold text-gray-900 mt-2 truncate" title={value}>{value}</h3>
                </div>
                <div className={`p-3 rounded-lg flex-shrink-0 ${colors[color] || 'bg-gray-50'}`}>
                    <span className="text-2xl">{icon}</span>
                </div>
            </div>
            <div className="mt-4 flex items-center text-sm truncate">
                <span className="text-green-600 font-medium flex-shrink-0">{trend}</span>
                <span className="text-gray-400 ml-2 truncate">vs last month</span>
            </div>
        </div>
    );
}

function SupportInbox({ tickets, onResolve }) {
    const [filter, setFilter] = useState('all'); 
    
    const filteredTickets = tickets.filter(t => {
        if (filter === 'open') return t.status !== 'resolved';
        if (filter === 'resolved') return t.status === 'resolved';
        return true;
    });

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex gap-2 bg-white p-1 rounded-lg w-fit shadow-sm border border-gray-200">
                {['all', 'open', 'resolved'].map(f => (
                    <button 
                        key={f} 
                        onClick={() => setFilter(f)} 
                        className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition ${filter === f ? 'bg-gray-900 text-white shadow' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            <div className="grid gap-4">
                {filteredTickets.map(ticket => (
                    <div key={ticket.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-pink-200 transition group">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${ticket.status === 'resolved' ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`}></div>
                                <h4 className="font-bold text-gray-900 text-lg">{ticket.subject}</h4>
                                <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full uppercase tracking-wide font-bold">{ticket.category || 'General'}</span>
                            </div>
                            <span className="text-xs text-gray-400">{ticket.createdAt?.seconds ? new Date(ticket.createdAt.seconds * 1000).toLocaleString() : 'Just now'}</span>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg text-gray-700 text-sm mb-4 leading-relaxed">
                            {ticket.message}
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>👤 {ticket.userName || ticket.email}</span>
                                <span>•</span>
                                <span>{ticket.email}</span>
                            </div>
                            {ticket.status !== 'resolved' ? (
                                <button 
                                    onClick={() => onResolve(ticket.id)}
                                    className="bg-green-100 text-green-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-green-200 transition flex items-center gap-2"
                                >
                                    <span>✓</span> Mark Resolved
                                </button>
                            ) : (
                                <span className="text-green-600 text-sm font-bold flex items-center gap-1">
                                    <span>✓</span> Resolved
                                </span>
                            )}
                        </div>
                    </div>
                ))}
                {filteredTickets.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                        <p>No tickets found in this category.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function UserTable({ users, onBan }) {
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');

    const filtered = users.filter(u => {
        const matchesSearch = u.email.toLowerCase().includes(search.toLowerCase()) || 
                              (u.vendorName || '').toLowerCase().includes(search.toLowerCase());
        const matchesRole = roleFilter === 'all' || 
                            (roleFilter === 'vendor' && u.isVendor) || 
                            (roleFilter === 'planner' && !u.isVendor);
        return matchesSearch && matchesRole;
    });

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-fade-in">
            <div className="p-4 border-b border-gray-200 flex gap-4 justify-between items-center flex-wrap">
                <input 
                    type="text" 
                    placeholder="Search users by email or name..." 
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                />
                <select 
                    value={roleFilter} 
                    onChange={e => setRoleFilter(e.target.value)} 
                    className="px-4 py-2 border rounded-lg text-sm bg-white cursor-pointer hover:bg-gray-50"
                >
                    <option value="all">All Users</option>
                    <option value="vendor">Vendors</option>
                    <option value="planner">Planners</option>
                </select>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-xs">
                        <tr>
                            <th className="px-6 py-4">User Info</th>
                            <th className="px-6 py-4">Role</th>
                            <th className="px-6 py-4">Joined</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map(user => (
                            <tr key={user.id} className="hover:bg-gray-50 transition">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-500 overflow-hidden">
                                            {user.profilePicUrl ? (
                                                <img src={user.profilePicUrl} alt="" className="w-full h-full object-cover" />
                                            ) : (user.email[0] || 'U').toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900">{user.vendorName || user.firstName || 'User'}</p>
                                            <p className="text-gray-500 text-xs">{user.email}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    {user.isVendor ? (
                                        <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-bold">
                                            <span>🏪</span> Vendor
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-bold">
                                            <span>👤</span> Planner
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-gray-500">
                                    {new Date(user.createdAt?.seconds * 1000 || Date.now()).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4">
                                    {user.isBanned ? (
                                        <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">Suspended</span>
                                    ) : (
                                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">Active</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <button 
                                        onClick={() => onBan(user.id, user.isBanned)}
                                        className={`text-xs font-bold px-3 py-1 rounded border transition ${user.isBanned ? 'border-green-300 text-green-600 hover:bg-green-50' : 'border-red-200 text-red-500 hover:bg-red-50'}`}
                                    >
                                        {user.isBanned ? 'Unban User' : 'Suspend User'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function ListingTable({ listings, onDelete, onToggleFeatured, userMap }) {
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');

    const filtered = listings.filter(l => {
        const matchesSearch = l.title.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || l.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const categories = ['all', ...new Set(listings.map(l => l.category))];

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-fade-in">
            <div className="p-4 border-b border-gray-200 flex gap-4 flex-wrap justify-between">
                <input 
                    type="text" 
                    placeholder="Search listings..." 
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="flex-grow max-w-sm px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none"
                />
                <select 
                    value={categoryFilter} 
                    onChange={e => setCategoryFilter(e.target.value)} 
                    className="px-4 py-2 border rounded-lg text-sm bg-white capitalize cursor-pointer hover:bg-gray-50"
                >
                    {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>)}
                </select>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-xs">
                        <tr>
                            <th className="px-6 py-4">Listing</th>
                            <th className="px-6 py-4">Vendor</th>
                            <th className="px-6 py-4">Category</th>
                            <th className="px-6 py-4">Price</th>
                            <th className="px-6 py-4 text-center">Featured</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map(item => (
                            <tr key={item.id} className="hover:bg-gray-50 transition">
                                <td className="px-6 py-4 flex items-center gap-3">
                                    <img src={item.imageUrls?.[0] || 'https://placehold.co/40'} className="w-10 h-10 rounded object-cover bg-gray-100 border border-gray-200" alt="" />
                                    <span className="font-medium text-gray-900 truncate max-w-[200px]">{item.title}</span>
                                </td>
                                <td className="px-6 py-4 text-gray-600 text-xs">
                                    {userMap[item.vendorId]?.vendorName || 'Unknown Vendor'}
                                </td>
                                <td className="px-6 py-4 capitalize text-gray-600">{item.category}</td>
                                <td className="px-6 py-4 font-bold text-gray-800">${item.price}</td>
                                <td className="px-6 py-4 text-center">
                                    <button 
                                        onClick={() => onToggleFeatured(item.id, item.isFeatured)} 
                                        className={`text-xl focus:outline-none transition-transform active:scale-125 ${item.isFeatured ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'}`}
                                        title={item.isFeatured ? "Remove from Featured" : "Add to Featured"}
                                    >
                                        ★
                                    </button>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <button onClick={() => onDelete(item.id)} className="text-gray-400 hover:text-red-600 font-bold p-1 rounded-full hover:bg-red-50 transition">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AdminSettings({ db }) {
    const [config, setConfig] = useState({ 
        platformFee: 10, 
        maintenanceMode: false,
        supportEmail: 'support@cacti-interactive.com',
        announcement: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const docRef = doc(db, 'system', 'config');
                const snap = await getDoc(docRef);
                if (snap.exists()) {
                    setConfig(prev => ({ ...prev, ...snap.data() }));
                }
            } catch (error) {
                if (error.code !== 'permission-denied') {
                    console.error("Error fetching config:", error);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchConfig();
    }, [db]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await setDoc(doc(db, 'system', 'config'), config, { merge: true });
            toast.success("System settings updated successfully.");
        } catch (error) {
            console.error("Error saving settings:", error);
            if (error.code === 'permission-denied') {
                toast.error("Permission denied: You do not have access to modify system settings.");
            } else {
                toast.error("Failed to save settings.");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading settings...</div>;

    return (
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-sm border border-gray-200 animate-fade-in">
            <h3 className="text-xl font-bold text-gray-900 mb-6 border-b pb-4">Platform Configuration</h3>
            
            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Service Fee Percentage (%)</label>
                    <p className="text-xs text-gray-500 mb-2">This fee is applied to all bookings processed through the platform.</p>
                    <input 
                        type="number" 
                        value={config.platformFee} 
                        onChange={e => setConfig({...config, platformFee: parseFloat(e.target.value)})}
                        className="w-full p-2 border rounded-lg max-w-xs"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Support Contact Email</label>
                    <input 
                        type="email" 
                        value={config.supportEmail} 
                        onChange={e => setConfig({...config, supportEmail: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                    />
                </div>

                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Global Announcement Banner</label>
                    <p className="text-xs text-gray-500 mb-2">Text entered here will appear at the top of the site for all users. Leave empty to hide.</p>
                    <input 
                        type="text" 
                        value={config.announcement} 
                        onChange={e => setConfig({...config, announcement: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        placeholder="e.g. Happy Holidays! 10% off all rentals."
                    />
                </div>

                <div className="flex items-center justify-between bg-red-50 p-4 rounded-lg border border-red-100">
                    <div>
                        <span className="font-bold text-red-800 block">Maintenance Mode</span>
                        <span className="text-xs text-red-600">Prevents users from logging in or booking items. Admin access remains.</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={config.maintenanceMode} onChange={e => setConfig({...config, maintenanceMode: e.target.checked})} className="sr-only peer" />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                </div>

                <div className="pt-6 border-t flex justify-end">
                    <button 
                        onClick={handleSave} 
                        disabled={saving}
                        className="bg-gray-900 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-gray-800 disabled:opacity-50 transition"
                    >
                        {saving ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>
            </div>
        </div>
    );
}